/**
 * Custom title bar for the borderless Windows desktop (Tauri) build.
 *
 * The Windows shell runs with native decorations off (see src-tauri) so the
 * app owns its whole surface — no OS menu strip, an immersive frame. This bar
 * provides the drag region, the app identity, the window controls, and an app
 * menu housing the actions the native menu used to carry (check for updates,
 * launch at login). Renders null everywhere except the Windows Tauri runtime,
 * so it is safe to mount unconditionally at the React root.
 */
import { useEffect, useState } from "react";
import { Minus, Square, Copy, X, MoreHorizontal } from '@/shared/ui/icons';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from "../../shared/ui/dropdown-menu";
import {
  isWindowsDesktop,
  winMinimize,
  winToggleMaximize,
  winClose,
  winStartResize,
  onWindowMaximizeChange,
  winGetLaunchAtLogin,
  winSetLaunchAtLogin,
  type WindowResizeDirection,
} from "../tauri/windowChrome";
import { checkForUpdatesInteractive } from "../tauri/tauriUpdater";

const RESIZE_EDGES: { dir: WindowResizeDirection; className: string }[] = [
  { dir: "North", className: "qs-win-resize qs-win-resize--n" },
  { dir: "South", className: "qs-win-resize qs-win-resize--s" },
  { dir: "East", className: "qs-win-resize qs-win-resize--e" },
  { dir: "West", className: "qs-win-resize qs-win-resize--w" },
  { dir: "NorthWest", className: "qs-win-resize qs-win-resize--nw" },
  { dir: "NorthEast", className: "qs-win-resize qs-win-resize--ne" },
  { dir: "SouthWest", className: "qs-win-resize qs-win-resize--sw" },
  { dir: "SouthEast", className: "qs-win-resize qs-win-resize--se" },
];

export function WindowTitleBar() {
  const [enabled] = useState(isWindowsDesktop);
  const [maximized, setMaximized] = useState(false);
  const [launchAtLogin, setLaunchAtLogin] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let dispose = () => {};
    void onWindowMaximizeChange(setMaximized).then((fn) => {
      dispose = fn;
    });
    void winGetLaunchAtLogin().then(setLaunchAtLogin);
    return () => dispose();
  }, [enabled]);

  if (!enabled) return null;

  const handleLaunchAtLogin = async (next: boolean) => {
    // Optimistic flip, then reconcile with the value the OS actually reports.
    setLaunchAtLogin(next);
    setLaunchAtLogin(await winSetLaunchAtLogin(next));
  };

  return (
    <>
      {/* Resize grips — a borderless window has no native ones on Windows. */}
      {!maximized &&
        RESIZE_EDGES.map(({ dir, className }) => (
          <div
            key={dir}
            className={className}
            aria-hidden="true"
            onPointerDown={(e) => {
              if (e.button !== 0) return;
              void winStartResize(dir);
            }}
          />
        ))}

      <div className="qs-win-titlebar">
        <div className="qs-win-titlebar__identity" aria-hidden="true">
          <span className="qs-win-titlebar__logo" aria-hidden="true" />
          <span className="qs-win-titlebar__title">Quantis</span>
        </div>

        <div className="qs-win-titlebar__controls">
          <DropdownMenu>
            <DropdownMenuTrigger
              className="qs-win-btn qs-win-btn--menu"
              aria-label="App menu"
            >
              <MoreHorizontal size={16} strokeWidth={2} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={6}>
              <DropdownMenuItem onSelect={() => void checkForUpdatesInteractive()}>
                Check for Updates...
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={launchAtLogin}
                onCheckedChange={(v) => void handleLaunchAtLogin(Boolean(v))}
              >
                Launch at Login
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            type="button"
            className="qs-win-btn"
            aria-label="Minimize"
            onClick={() => void winMinimize()}
          >
            <Minus size={16} strokeWidth={2} />
          </button>
          <button
            type="button"
            className="qs-win-btn"
            aria-label={maximized ? "Restore" : "Maximize"}
            onClick={() => void winToggleMaximize()}
          >
            {maximized ? (
              <Copy size={13} strokeWidth={2} />
            ) : (
              <Square size={13} strokeWidth={2} />
            )}
          </button>
          <button
            type="button"
            className="qs-win-btn qs-win-btn--close"
            aria-label="Close"
            onClick={() => void winClose()}
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>
      </div>
    </>
  );
}
